import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { QuoteEditor } from "./QuoteEditor";
import { api } from "../../api/client";
import type { CatalogHit, QuoteDetail, TemplateSummary } from "../../api/types";
import type { SectionNavItem } from "../../layout/SectionNav";
import type { CommandLineTarget } from "../command/CommandPalette";

// /quotes/:id 的載入層。取單子、處理載入與失敗,拿到之後才把編輯器掛上去 ——
// 編輯器一啟動就把回應複製成本地草稿,所以它必須拿到完整的資料才建得起來。
//
// 網址上的 ?apply_template=N 是「換頁之後接著套用範本」的接力棒:
// 範本頁與新增報價單頁都靠它把使用者選好的範本交到編輯器手上。
// 用完就把參數清掉,免得重新整理又跳一次對話框。

export interface QuoteEditorPageProps {
  /** 把本單章節回報給外框的側邊欄導覽。 */
  onSections?: (sections: SectionNavItem[]) => void;
  /** 把本單明細回報給外框的指令面板,⌘K 才搜得到本單的明細。 */
  onLines?: (lines: CommandLineTarget[]) => void;
  /** 把「套用範本」登記給外框的指令面板。 */
  onRegisterApplyTemplate?: (handler: ((template: TemplateSummary) => void) | null) => void;
  /** 把「品項庫帶入目前章節」登記給外框的指令面板。 */
  onRegisterPickCatalog?: (handler: ((hit: CatalogHit) => void) | null) => void;
}

export function QuoteEditorPage({
  onSections,
  onLines,
  onRegisterApplyTemplate,
  onRegisterPickCatalog,
}: QuoteEditorPageProps) {
  const { id } = useParams();
  const navigate = useNavigate();
  const quoteId = Number(id);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedRaw = Number(searchParams.get("apply_template"));
  const requestedTemplateId = Number.isFinite(requestedRaw) && requestedRaw > 0 ? requestedRaw : null;

  const clearTemplateRequest = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("apply_template");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["quote", quoteId],
    queryFn: () => api.get<QuoteDetail>(`/quotes/${quoteId}`),
    enabled: Number.isFinite(quoteId) && quoteId > 0,
  });

  // 離開這一頁就把章節導覽與本單明細清掉,
  // 免得清單頁的側邊欄還留著上一單的章節、⌘K 還搜得到上一單的明細。
  useEffect(() => {
    return () => onSections?.([]);
  }, [onSections]);

  useEffect(() => {
    return () => onLines?.([]);
  }, [onLines]);

  if (!Number.isFinite(quoteId) || quoteId <= 0) {
    return <p className="m-0 text-sm text-[var(--danger)]">網址裡的報價單編號不正確。</p>;
  }
  if (isLoading) {
    return <p className="m-0 text-sm text-[var(--text-3)]">載入中…</p>;
  }
  if (error || !data) {
    return (
      <p role="alert" className="m-0 text-sm text-[var(--danger)]">
        {error instanceof Error ? error.message : "這份報價單載入失敗"}
      </p>
    );
  }

  return (
    <QuoteEditor
      // 換一張單就換一份草稿:key 換掉,編輯器重新以新的資料建立狀態。
      key={data.id}
      quote={data}
      onSectionsChange={onSections}
      onLinesChange={onLines}
      requestedTemplateId={requestedTemplateId}
      onTemplateRequestHandled={clearTemplateRequest}
      onRegisterApplyTemplate={onRegisterApplyTemplate}
      onRegisterPickCatalog={onRegisterPickCatalog}
      onSaved={(saved) => {
        queryClient.setQueryData(["quote", quoteId], saved);
        queryClient.invalidateQueries({ queryKey: ["quotes"] });
      }}
      onArchived={(url) => navigate(url)}
    />
  );
}
